/**
 * Time Conversion Utilities
 * Handles conversion between decimal hours, hh:mm format, and various time representations
 */

/**
 * Helper function to convert decimal hours to hh:mm format
 * Example: 8.5 hours = 08:30 (8 hours + 0.5 * 60 = 30 minutes)
 * @param decimalHours - Decimal representation of hours (e.g., 8.5)
 * @returns Time in hh:mm format (e.g., "08:30")
 */
export const hoursToTime = (decimalHours: number): string => {
  if (!decimalHours || decimalHours <= 0) return '00:00';
  
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  
  // Format with leading zeros
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Helper function to normalize incorrectly formatted time strings
 * Converts: 08.50 -> 08:30, 08.75 -> 08:45, etc.
 * @param timeStr - Time string that may contain decimal notation
 * @returns Normalized time string in hh:mm format
 */
export const normalizeTimeString = (timeStr: string): string => {
  if (!timeStr || timeStr.trim() === '') return '';
  
  // Check if string contains decimal point (08.50, 08.75, etc.)
  if (timeStr.includes('.') && !timeStr.includes(':')) {
    const decimalValue = parseFloat(timeStr);
    if (!isNaN(decimalValue)) {
      return hoursToTime(decimalValue);
    }
  }
  
  // Return original string if no conversion needed
  return timeStr;
};

/**
 * Helper function to convert hoursPerDayType to decimal hours
 * Supports multiple formats:
 * - hh:mm format (e.g., "08:30")
 * - Decimal with . or , separator (e.g., "8.20" or "8,20")
 * - Plain number (e.g., "8")
 * @param value - Time value in various formats
 * @returns Decimal hours
 */
export const convertToDecimalHours = (value: string): number => {
  if (!value) return 0;
  
  // Check if it's time format (hh:mm)
  if (value.includes(':')) {
    const [hours, minutes] = value.split(':').map(Number);
    return hours + minutes / 60;
  }
  
  // Check if it's decimal with . or , separator (like 8.20 or 8,20)
  if (value.includes('.') || value.includes(',')) {
    const normalizedValue = value.replace(',', '.');
    const decimalValue = parseFloat(normalizedValue);
    
    // Check if the decimal part represents minutes (0-59) instead of fractional hours
    // Example: 8.20 should be treated as 8 hours 20 minutes = 8.333... hours
    // But 8.5 should be treated as 8.5 hours (8 hours 30 minutes)
    const parts = normalizedValue.split('.');
    if (parts.length === 2) {
      const decimalPart = parseInt(parts[1]);
      // If decimal part is between 0-59, treat as minutes
      if (decimalPart >= 0 && decimalPart <= 59) {
        const hours = parseInt(parts[0]);
        return hours + decimalPart / 60;
      }
    }
    
    return decimalValue;
  }
  
  // Plain number without separator
  return parseFloat(value);
};

/**
 * Helper function to convert decimal hours to hh:mm format
 * @param decimalHours - Decimal representation of hours
 * @returns Time in hh:mm format
 */
export const convertToTimeFormat = (decimalHours: number): string => {
  return hoursToTime(decimalHours);
};

/**
 * Convert time string (hh:mm) to decimal hours
 * @param timeStr - Time in hh:mm format
 * @returns Decimal hours
 */
export const timeToDecimalHours = (timeStr: string): number => {
  if (!timeStr || !timeStr.includes(':')) return 0;
  
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return 0;
  
  return hours + minutes / 60;
};

/**
 * Validate time format (hh:mm)
 * @param timeStr - Time string to validate
 * @returns true if valid hh:mm format
 */
export const isValidTimeFormat = (timeStr: string): boolean => {
  if (!timeStr) return false;
  
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(timeStr);
};

/**
 * Format minutes to hh:mm
 * @param totalMinutes - Total minutes
 * @returns Time in hh:mm format
 */
export const minutesToTime = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Parse time string to total minutes
 * @param timeStr - Time in hh:mm format
 * @returns Total minutes
 */
export const timeToMinutes = (timeStr: string): number => {
  if (!timeStr || !timeStr.includes(':')) return 0;
  
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return 0;

  return hours * 60 + minutes;
};

/**
 * @param hours - Hours value
 * @param minutes - Minutes value (0-59)
 * @param maxHours - Maximum allowed hours after carrying over (default 23)
 * @returns Hours and minutes snapped to a quarter-hour
 */
export const roundToQuarterHour = (
  hours: number,
  minutes: number,
  maxHours: number = 23
): { hours: number; minutes: number } => {
  let roundedHours = hours;
  let roundedMinutes = Math.round(minutes / 15) * 15;
  if (roundedMinutes === 60) {
    roundedMinutes = 0;
    roundedHours += 1;
  }

  if (roundedHours > maxHours) {
    roundedHours = maxHours;
    roundedMinutes = 45;
  }

  return { hours: roundedHours, minutes: roundedMinutes };
};
